import React, { useRef, useState } from "react";
import checkIcon from "../../assets/check.png";
import circleIcon from "../../assets/circle.png";
import xIcon from "../../assets/close.png";

const AutoCheckbox = ({
  field,
  updateField,
  selectField,
  onContainerMouseDown,
  onContainerMouseUp,
  isSelected,
  zoom,
}) => {
  const {
    id,
    checked = true,
    baseWidth = 30,
    width: fieldWidth,
    height: fieldHeight,
    checkboxStyle = "check",
  } = field;

  // use whichever size is set (width overrides baseWidth)
  const logicalSize = fieldWidth ?? fieldHeight ?? baseWidth;
  const wPx = logicalSize * zoom;
  const hPx = logicalSize * zoom;
  const leftPx = field.baseLeft * zoom;
  const topPx = field.baseTop * zoom;

  const startPos = useRef({ x: 0, y: 0 });
  const startSize = useRef({ w: logicalSize, h: logicalSize });

  const [isHovered, setIsHovered] = useState(false);

  const handleFieldMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectField(id);
    onContainerMouseDown(e, id);
  };

  const handleFieldMouseUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onContainerMouseUp(e, id);
  };

  // begin resize: capture start coords + size
  const handleResizePointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    selectField(id);
    startPos.current = { x: e.clientX, y: e.clientY };
    startSize.current = { w: logicalSize, h: logicalSize };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  // on move: update both width/height AND baseWidth/baseHeight
  const handleResizePointerMove = (e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;

    const dx = (e.clientX - startPos.current.x) / zoom;
    const dy = (e.clientY - startPos.current.y) / zoom;
    const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;

    const newSize = Math.max(10, startSize.current.w + delta);

    updateField(id, {
      width: newSize,
      height: newSize,
      baseWidth: newSize,
      baseHeight: newSize,
    });
  };

  // release capture
  const handleResizePointerUp = (e) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  // choose icon based on style
  const renderIcon = () => {
    if (!checked) return null;
    const size = logicalSize * zoom;
    const style = { width: size, height: size, objectFit: "contain" };
    let src;
    switch (checkboxStyle) {
      case "check":
        src = checkIcon;
        break;
      case "circle":
        src = circleIcon;
        break;
      case "x":
        src = xIcon;
        break;
      default:
        return null;
    }
    return <img src={src} alt={checkboxStyle} style={style} />;
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleFieldMouseDown}
      onMouseUp={handleFieldMouseUp}
      onClick={(e) => {
        e.stopPropagation();
        selectField(id);
      }}
      style={{
        position: "absolute",
        left: leftPx,
        top: topPx,
        width: wPx,
        height: hPx,
        boxSizing: "border-box",
        border:
          isSelected || isHovered
            ? "1px dashed #60B5FF"
            : "1px solid transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        userSelect: "none",
      }}
    >
      {renderIcon()}

      {isSelected && (
        <div
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          style={{
            position: "absolute",
            width: 12,
            height: 12,
            right: 0,
            bottom: 0,
            transform: "translate(50%, 50%)",
            cursor: "se-resize",
            background: "#9EC6F3",
            border: "1px solid #60B5FF",
            boxSizing: "border-box",
            zIndex: 10,
          }}
        />
      )}
    </div>
  );
};

export default AutoCheckbox;
